import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { usePendingCount } from '../../hooks/usePendingCount'
import AccountSelector from '../Account/AccountSelector'
import { LanguageSwitcher } from '../shared'

function Header() {
  const location = useLocation()
  const { user, logout } = useAuth()
  const { t } = useTranslation('common')
  const { count: pendingCount } = usePendingCount()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Focus management refs for accessibility
  const mobileMenuButtonRef = useRef(null)
  const mobileMenuCloseButtonRef = useRef(null)

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    // Check for exact match or sub-routes (e.g., /ecommerce/feeds matches /ecommerce)
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  // Focus management for accessibility
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Focus the close button when menu opens
      mobileMenuCloseButtonRef.current?.focus()
    }
  }, [isMobileMenuOpen])

  // Escape key handler for accessibility
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
        mobileMenuButtonRef.current?.focus()
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isMobileMenuOpen])

  // Memoized navigation items for performance
  const navigationItems = useMemo(() => [
    { path: '/', label: t('nav.dashboard') },
    { path: '/create', label: t('nav.create') },
    { path: '/approval', label: t('nav.approval'), badge: pendingCount > 0 ? pendingCount : null },
    { path: '/schedule', label: t('nav.schedule') },
    { path: '/analytics', label: t('nav.analytics') },
    { path: '/ecommerce', label: t('nav.ecommerce', 'E-Commerce') },
    { path: '/accounts', label: t('nav.accounts') },
  ], [t, pendingCount])

  return (
    <header className="sticky top-0 z-30">
      {/* Primary Header - Logo & User Menu */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">SP</span>
                </div>
                <span className="text-xl font-bold text-gray-900">{t('brand.name')}</span>
              </Link>
            </div>

            {/* User Menu - Desktop Only */}
            <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                {/* Account Selector */}
                <AccountSelector />

                <div className="h-6 w-px bg-gray-300" />

                {/* Language Switcher */}
                <LanguageSwitcher variant="compact" dropdownAlign="right" />

                <div className="h-6 w-px bg-gray-300" />

                <span className="text-sm text-gray-600">
                  {user.email || user.username}
                </span>
                <Link
                  to="/settings"
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {t('nav.settings')}
                </Link>
                <button
                  onClick={logout}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {t('nav.logout')}
                </button>
              </>
            )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center">
              <button
                ref={mobileMenuButtonRef}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              >
                {isMobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Navigation - Main Nav Links */}
      <div className="hidden md:block bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <nav className="flex items-center h-14 -mb-px">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center px-4 h-full text-sm font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.label}
                {item.badge && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-white bg-red-600 rounded-full min-w-[1.25rem]">
                    {item.badge}
                  </span>
                )}
                {/* Active indicator */}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></span>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Drawer - Using React Portal for proper DOM placement */}
      {isMobileMenuOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div
            className="fixed top-0 right-0 h-full w-80 max-w-[80vw] bg-white shadow-xl z-50 transform transition-transform duration-300 md:hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
          >
            <div className="flex flex-col h-full">
              {/* Header with close button */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <span id="mobile-menu-title" className="text-lg font-semibold text-gray-900">
                  {t('nav.menu')}
                </span>
                <button
                  ref={mobileMenuCloseButtonRef}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  aria-label={t('nav.closeMenu')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User section */}
              {user && (
                <div className="p-4 border-b border-gray-200 space-y-4">
                  <div className="text-sm text-gray-600">
                    {user.email || user.username}
                  </div>
                  <AccountSelector />
                  <LanguageSwitcher variant="full" dropdownAlign="left" className="w-full" />
                </div>
              )}

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-4">
                <div className="space-y-1">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        {item.label}
                        {item.badge && (
                          <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </span>
                    </Link>
                  ))}
                </div>
              </nav>

              {/* Settings & Logout buttons */}
              {user && (
                <div className="p-4 border-t border-gray-200 space-y-2">
                  <Link
                    to="/settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors text-center"
                  >
                    {t('nav.settings')}
                  </Link>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      logout()
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </header>
  )
}

export default Header
